import React, { useId, useState } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

// Single-select searchable combobox. options: [{value,label,flag}]
export function Combobox({ options, value, onChange, placeholder = "Select…", searchPlaceholder = "Search…", emptyText = "No country found.", testId, id }) {
  const generatedId = useId();
  const tid = testId || `country-${generatedId}`;
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" id={id} data-testid={tid} aria-expanded={open} aria-label={placeholder} variant="outline" role="combobox"
          className="w-full justify-between bg-black/[0.03] border-black/10 hover:bg-black/[0.05] font-normal">
          <span className={cn("min-w-0 truncate", !selected && "text-neutral-500")}>
            {selected ? <span className="flex items-center gap-2"><span className="text-lg">{selected.flag}</span> {selected.label}</span> : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent data-testid={`${tid}-menu`} className="w-[--radix-popover-trigger-width] p-0 bg-white border-black/10" align="start">
        <Command className="bg-transparent">
          <CommandInput data-testid={`${tid}-search`} placeholder={searchPlaceholder} className="text-sm" />
          <CommandList>
            <CommandEmpty data-testid={`${tid}-empty`}>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((o) => (
                <CommandItem data-testid={`${tid}-option-${o.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={o.value} value={o.label} onSelect={() => { onChange(o.value); setOpen(false); }}
                  className="cursor-pointer aria-selected:bg-orange-500/20">
                  <span className="text-lg mr-2">{o.flag}</span>{o.label}
                  <Check className={cn("ml-auto h-4 w-4", value === o.value ? "opacity-100 text-orange-500" : "opacity-0")} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Multi-select searchable combobox. values: string[]
export function MultiCombobox({ options, values, onChange, placeholder = "Select countries…" }) {
  const generatedId = useId();
  const tid = `countries-${generatedId}`;
  const [open, setOpen] = useState(false);
  const toggle = (v) => values.includes(v) ? onChange(values.filter((x) => x !== v)) : onChange([...values, v]);
  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button data-testid={tid} type="button" aria-expanded={open} variant="outline" role="combobox"
            className="w-full justify-between bg-black/[0.03] border-black/10 hover:bg-black/[0.05] font-normal">
            <span className={cn(!values.length && "text-neutral-500")}>{values.length ? `${values.length} selected` : placeholder}</span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent data-testid={`${tid}-menu`} className="w-[--radix-popover-trigger-width] p-0 bg-white border-black/10" align="start">
          <Command className="bg-transparent">
            <CommandInput data-testid={`${tid}-search`} placeholder="Search countries…" className="text-sm" />
            <CommandList>
              <CommandEmpty data-testid={`${tid}-empty`}>No country found.</CommandEmpty>
              <CommandGroup>
                {options.map((o) => (
                  <CommandItem data-testid={`${tid}-option-${o.value.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} key={o.value} value={o.label} onSelect={() => toggle(o.value)}
                    className="cursor-pointer aria-selected:bg-orange-500/20">
                    <span className="text-lg mr-2">{o.flag}</span>{o.label}
                    <Check className={cn("ml-auto h-4 w-4", values.includes(o.value) ? "opacity-100 text-orange-500" : "opacity-0")} />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {values.map((v) => (
            <span key={v} className="text-xs bg-orange-500/15 text-orange-300 border border-orange-500/30 rounded-full px-2.5 py-1 flex items-center gap-1">
              {v}<button data-testid={`${tid}-remove-${v.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} aria-label={`Remove ${v}`} type="button" onClick={() => toggle(v)}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
